import React, { useState } from 'react';
import { X, Check, ArrowRight, Target, Eye, FileText, Calendar, DollarSign, Plus, Filter, Search, Zap } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';

interface TendersOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const TendersOnboardingModal: React.FC<TendersOnboardingModalProps> = ({ isOpen, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { updateOnboardingStatus } = useTenant();

  const steps = [
    {
      title: "¡Bienvenido a Licitaciones!",
      subtitle: "Aquí encontrarás las oportunidades que coinciden con tu negocio",
      content: (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-6">
            <Target className="w-10 h-10 text-white" />
          </div>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            Las licitaciones que ves aquí son <strong>filtradas automáticamente</strong> según las líneas de negocio 
            que definiste en tu perfil. Usamos IA para que solo aparezcan las oportunidades que realmente te interesan.
          </p>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Zap className="h-5 w-5 text-green-600 mt-0.5" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-green-900 mb-1">
                  Filtrado Inteligente
                </h4>
                <p className="text-sm text-green-700">
                  El sistema busca automáticamente licitaciones que contengan las palabras clave 
                  de tus líneas de negocio en el título.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Botones Disponibles",
      subtitle: "Conoce las acciones que puedes realizar con cada licitación",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ver Detalles */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                  <Eye className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-blue-900">Ver Detalles</h3>
              </div>
              <p className="text-sm text-blue-700">
                Accede a toda la información completa de la licitación: documentos, 
                requisitos, fechas importantes y más.
              </p>
            </div>

            {/* Generar Oportunidad */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full mr-3">
                  <Plus className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-green-900">Generar Oportunidad</h3>
              </div>
              <p className="text-sm text-green-700">
                Crea una oportunidad de negocio para hacer seguimiento y gestión 
                de esta licitación específica.
              </p>
            </div>

            {/* Filtrar */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full mr-3">
                  <Filter className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="font-semibold text-purple-900">Filtrar</h3>
              </div>
              <p className="text-sm text-purple-700">
                Refina los resultados por fecha, monto, región, tipo de licitación 
                y otros criterios específicos.
              </p>
            </div>

            {/* Buscar */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-orange-100 rounded-full mr-3">
                  <Search className="w-4 h-4 text-orange-600" />
                </div>
                <h3 className="font-semibold text-orange-900">Buscar</h3>
              </div>
              <p className="text-sm text-orange-700">
                Busca licitaciones específicas por palabras clave, 
                organizaciones o términos particulares.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Flujo de una Licitación",
      subtitle: "Entiende el ciclo de vida completo de una licitación",
      content: (
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Publicación</h3>
              <p className="text-gray-600 mb-3">
                La organización publica la licitación con todos los detalles, documentos y requisitos.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>📋 Incluye:</strong> Bases técnicas, pliegos, cronograma, montos estimados
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full">
                <Calendar className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Período de Consultas</h3>
              <p className="text-gray-600 mb-3">
                Tiempo para hacer preguntas, aclarar dudas y obtener información adicional.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>⏰ Importante:</strong> Respeta las fechas límite para consultas
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Presentación de Ofertas</h3>
              <p className="text-gray-600 mb-3">
                Envío de propuestas técnicas y económicas según los requisitos establecidos.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>💡 Tip:</strong> Prepara tu oferta con anticipación y revisa todos los documentos
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full">
                <Check className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4. Evaluación y Adjudicación</h3>
              <p className="text-gray-600 mb-3">
                La organización evalúa las ofertas y adjudica a la propuesta ganadora.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-800">
                  <strong>📊 Resultado:</strong> Se publica el resultado y se notifica a los participantes
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Generar Oportunidad",
      subtitle: "Convierte una licitación en una oportunidad de negocio",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-4">
              <Plus className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">¿Qué es una Oportunidad?</h3>
            <p className="text-gray-600">
              Una oportunidad te permite hacer seguimiento detallado de una licitación específica, 
              gestionar tu participación y organizar tu estrategia de negocio.
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">✅ Beneficios de crear una oportunidad:</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• <strong>Seguimiento personalizado</strong> de la licitación</li>
                <li>• <strong>Recordatorios automáticos</strong> de fechas importantes</li>
                <li>• <strong>Gestión de documentos</strong> y requisitos</li>
                <li>• <strong>Análisis de competencia</strong> y mercado</li>
                <li>• <strong>Historial completo</strong> de tu participación</li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">🎯 Cuándo generar una oportunidad:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• La licitación se ajusta a tu línea de negocio</li>
                <li>• Tienes capacidad técnica para ejecutarla</li>
                <li>• El monto es atractivo para tu empresa</li>
                <li>• Las fechas te permiten preparar una buena oferta</li>
                <li>• Quieres hacer seguimiento detallado</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2">💡 Proceso recomendado:</h4>
              <ol className="text-sm text-yellow-700 space-y-1">
                <li>1. <strong>Revisa los detalles</strong> de la licitación</li>
                <li>2. <strong>Evalúa la viabilidad</strong> técnica y económica</li>
                <li>3. <strong>Genera la oportunidad</strong> si decides participar</li>
                <li>4. <strong>Haz seguimiento</strong> de fechas y requisitos</li>
                <li>5. <strong>Prepara tu oferta</strong> con tiempo suficiente</li>
              </ol>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "¡Listo para comenzar!",
      subtitle: "Ya conoces todo lo necesario para usar la sección de licitaciones",
      content: (
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-4">
            <Check className="w-10 h-10 text-white" />
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">¡Explora las oportunidades!</h3>
            <p className="text-gray-600 text-lg">
              Ahora puedes navegar por las licitaciones, usar los filtros, buscar oportunidades específicas 
              y generar oportunidades de negocio para hacer seguimiento detallado.
            </p>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-700">
                <strong>🎯 Recuerda:</strong> Las licitaciones se filtran automáticamente según tus líneas de negocio. 
                Si no ves licitaciones relevantes, revisa tu configuración en "Mi Perfil".
              </p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    if (dontShowAgain) {
      try {
        await updateOnboardingStatus('Onboarding2', true);
      } catch (error) {
        console.error('Error updating onboarding status:', error);
      }
    }
    onComplete();
  };

  const handleSkip = async () => {
    if (dontShowAgain) {
      try {
        await updateOnboardingStatus('Onboarding2', true);
      } catch (error) {
        console.error('Error updating onboarding status:', error);
      }
    }
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{steps[currentStep].title}</h2>
              <p className="text-green-100 text-sm">{steps[currentStep].subtitle}</p>
            </div>
            <button
              onClick={handleSkip}
              className="text-green-200 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    index <= currentStep ? 'bg-white' : 'bg-green-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-green-100 text-xs mt-2">
              Paso {currentStep + 1} de {steps.length}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {steps[currentStep].content}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="dontShowAgainTenders"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="dontShowAgainTenders" className="ml-2 text-sm text-gray-600">
                No mostrar más este tutorial
              </label>
            </div>
            
            <div className="flex space-x-3">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Anterior
                </button>
              )}
              
              <button
                onClick={handleNext}
                className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center"
              >
                {currentStep === steps.length - 1 ? 'Comenzar' : 'Siguiente'}
                {currentStep < steps.length - 1 && <ArrowRight className="w-4 h-4 ml-2" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TendersOnboardingModal;
